import React, { ReactElement } from "react"
import { FormControlLabel, Radio, Skeleton } from "@mui/material"
import styled, { css } from "styled-components"
import { theme } from "../../../theme"

export interface RadioCardProps {
  name: string
  onChange?: (value: boolean) => void
  imageUrl?: string
  icon?: ReactElement
  checked?: boolean
  disabled?: boolean
  label?: string
  hasError?: boolean
  loading?: boolean
}

export const RadioCard = (props: RadioCardProps) => {
  const {
    name,
    onChange,
    imageUrl,
    icon,
    checked = false,
    disabled = false,
    label,
    hasError = false,
    loading = false,
  } = props

  return (
    <Card $checked={checked} $hasError={hasError} $showImage={!!imageUrl || loading}>
      <Label htmlFor={name} $disabled={!!disabled} />
      {!!imageUrl && !loading && <ImageContainer $url={imageUrl} />}
      {!!loading && <Skeleton variant="rectangular" height={144} width="100%" />}
      <FormControlLabel
        control={
          <Radio
            disabled={disabled || loading}
            color="primary"
            name={name}
            id={name}
            checked={checked}
            onChange={() => !!onChange && onChange(true)}
          />
        }
        label={loading ? <Skeleton variant="rectangular" height={24} width="100%" /> : label}
      />
      {!imageUrl && !!icon && !loading && <IconWrapper>{icon}</IconWrapper>}
    </Card>
  )
}

const rowStyle = css`
  grid-auto-flow: row;
  grid-auto-rows: max-content auto;
  align-items: flex-start;
`

const columnStyle = css`
  grid-auto-flow: column;
  grid-auto-columns: auto max-content;
  align-items: center;
`

const getCardBorderStyle = ($checked?: boolean, $hasError?: boolean) => {
  if ($checked) {
    return `1px solid ${theme.color.primary}`
  }
  if ($hasError) {
    return `1px solid ${theme.color.error}`
  }
  return `1px solid ${theme.color.white1}`
}

const Card = styled.span<{ $checked: boolean; $showImage: boolean; $hasError?: boolean }>`
  max-width: 242px;
  padding: ${theme.spacing.l};
  position: relative;
  display: grid;
  grid-gap: ${theme.spacing.m};
  ${(p) => (p.$showImage === true ? rowStyle : columnStyle)}

  border: ${(p) => getCardBorderStyle(p.$checked, p.$hasError)};
  outline: 1px solid ${(p) => (p.$checked === true ? theme.color.primary : "transparent")};

  .MuiFormControlLabel-root {
    margin-right: 0;
    .MuiFormControlLabel-label {
      width: 100%;
    }
  }
`

const Label = styled.label<{ $disabled: boolean }>`
  cursor: ${(p) => (p.$disabled === true ? "normal" : "pointer")};
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const ImageContainer = styled.div<{ $url?: string }>`
  height: 144px;
  max-width: 100%;
  background: url(${(p) => p.$url}) no-repeat center;
  background-size: contain;

  + .MuiFormControlLabel-root {
    align-items: flex-start !important;

    .MuiTypography-root {
      padding-top: ${theme.spacing.m};
    }
  }
`

const IconWrapper = styled.span`
  color: ${theme.color.primary};
`
